import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Home, LogOut, UserCircle } from 'lucide-react';
import './DashboardSidebar.css';
import logo from '../components/assets/logo.png';

// Single source of truth for the photographer dashboard's sidebar nav.
// Used by Upload.js, PhotographerRequests.js, Reviews.js and
// PhotographProfile.js so the four pages can never visually drift from
// each other again — one component, one stylesheet.
const NAV_ITEMS = [
  { key: 'upload', label: 'Upload', path: '/Upload' },
  { key: 'requests', label: 'Requests', path: '/PhotographerRequests' },
  { key: 'reviews', label: 'Reviews', path: '/Reviews' },
  { key: 'profile', label: 'Profile', path: '/PhotographProfile' },
];

const DashboardSidebar = ({
  activeItem,
  visible,
  isMobile,
  onToggle,
  onClose,
  userName,
  profilePhoto,
  onHome,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [avatarLoadError, setAvatarLoadError] = useState(false);

  useEffect(() => {
    setAvatarLoadError(false);
  }, [profilePhoto]);

  const goTo = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  const sidebarClass = isMobile
    ? `ds-sidebar ${visible ? 'show' : ''}`
    : `ds-sidebar ${visible ? '' : 'hidden'}`;

  return (
    <>
      <button
        type="button"
        className={`ds-toggle-btn ${visible ? 'active' : ''}`}
        onClick={onToggle}
        aria-label="Toggle navigation menu"
      >
        <span className="ds-toggle-icon">
          {visible ? <X size={20} /> : <Menu size={20} />}
        </span>
      </button>

      {isMobile && (
        <div className={`ds-overlay ${visible ? 'active' : ''}`} onClick={onClose}></div>
      )}

      <div className={sidebarClass}>
        <div className="ds-logo-container">
          <img src={logo} alt="LitStudio" className="ds-logo" />
        </div>

        <nav className="ds-nav-menu">
          {NAV_ITEMS.map((item) => (
            <div
              key={item.key}
              className={`ds-nav-item ${activeItem === item.key ? 'is-active' : ''}`}
              onClick={() => goTo(item.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goTo(item.path);
                }
              }}
            >
              <span>{item.label}</span>
            </div>
          ))}
        </nav>

        <div className="ds-user-profile">
          <div className="ds-user-info">
            <div className="ds-user-avatar">
              {profilePhoto && !avatarLoadError ? (
                <img
                  src={profilePhoto}
                  alt={userName}
                  className="ds-user-avatar-image"
                  onError={() => setAvatarLoadError(true)}
                />
              ) : (
                <UserCircle size={20} />
              )}
            </div>
            <div className="ds-welcome-text">
              <div>{userName}</div>
              <small>Photographer</small>
            </div>
          </div>

          <div className="ds-user-actions">
            <button type="button" onClick={onHome} title="Go to Home">
              <Home size={15} />
              <span>Home</span>
            </button>
            <button type="button" className="ds-logout-btn" onClick={onLogout} title="Logout">
              <LogOut size={15} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar;
